<template>
  <AsideLayout class="job-referral-editor">
    <template #aside>
      <div class="aside-head">
        <el-button class="back-job-list-btn" type="text" @click="handleBackJobList">{{ $t(`${i18nKeyPrefix}.btn.backJobList`) }}</el-button>
      </div>
      <div class="aside-body">
        <div class="referral-status-history">
          <div
            v-for="(history, index) in historyInfo"
            class="history-item"
            :key="index"
          >
            <div class="history-status">{{ $t(`${i18nKeyPrefix}.label.configStatusActionName.${getConfigStatusName(history.to_status)}`) }}</div>
            <div class="history-operator">{{ history.operator.name }}</div>
            <div class="history-time">{{ history.created_time|formatDateTime }}</div>
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <div class="referral-info-head">
        <div class="referral-title">
          <span>{{ $t(`${i18nKeyPrefix}.title`, {jobName}) }}</span>
          <span v-if="configStatusName">({{ $t(`${i18nKeyPrefix}.label.configStatus.${configStatusName}`) }})</span>
        </div>
      </div>
      <div class="referral-info-body">
        <div class="referral-editor adapt-wrapper">
          <div class="editor-line hire-number">
            <div class="line-label">
              <label>{{ $t(`${i18nKeyPrefix}.label.hireNumber`) }}</label>
            </div>
            <div class="line-content short-content">
              <NumberInput
                v-model="referralInfo.hireNumber"
                :disabled="isDisableAllInput && !isOnlyEnableHireNumberInput"
                :min="minHireNumber"
              />
            </div>
          </div>
          <div class="editor-line every-hire-cost">
            <div class="line-label">
              <label>{{ $t(`${i18nKeyPrefix}.label.everyHireCost`) }}</label>
              <el-tooltip effect="dark" placement="top" popper-class="referral-tip-popper" :content="$t(`${i18nKeyPrefix}.message.referralTip.everyHireCost`)" >
                <van-icon name="question-o" class="referral-tip-icon" />
              </el-tooltip>
            </div>
            <div class="line-content short-content">
              <ReferralInput
                v-model="referralInfo.everyHireCost.cash"
                type="cash"
                :disabled="isDisableAllInput"
                :min="100"
                :skip-validate="[0]"
              />
              <ReferralInput
                v-if="showPointsReferralConfig"
                v-model="referralInfo.everyHireCost.point"
                type="point"
                :disabled="isDisableAllInput"
                :min="100"
                :skip-validate="[0]"
              />
            </div>
          </div>
          <div v-if="!isLiteReferralConfig" class="editor-line only-employee">
            <div class="line-label">
              <label>{{ $t(`${i18nKeyPrefix}.label.onlyEmployeeReferral`) }}</label>
            </div>
            <div class="line-content short-content">
              <RectSwitch
                v-model="referralInfo.onlyEmployee"
                active-color="#0b8fce"
                inactive-color="#0b8fce"
                :disabled="isDisableAllInput"
              />
            </div>
          </div>
          <div class="editor-line hire-step-cost">
            <div class="line-label">
              <label>{{ $t(`${i18nKeyPrefix}.label.hireStepCost`) }}</label>
            </div>
            <div class="line-content">
              <div class="cost-config">
                <div
                  v-for="(group, index) in stepChooseInfo"
                  class="cost-config-group"
                  :key="index"
                >
                  <div class="step-choose-container">
                    <div v-for="(info, name) in group.choose" class="step-choose" :key="name">
                      <el-checkbox v-model="info.value" :disabled="isDisableAllInput" :style="{ '--color': info.color }">{{ $t(info.labelI18nKey) }}</el-checkbox>
                      <el-tooltip effect="dark" placement="top" popper-class="referral-tip-popper" :content="$t(info.tipI18nKey)" >
                        <van-icon name="question-o" class="referral-tip-icon" />
                      </el-tooltip>
                    </div>
                  </div>
                  <div v-if="Object.values(group.select).length > 0" class="type-select-container">
                    <ReferralTypeSelect
                      v-for="(info, name) in group.select"
                      v-model="info.value"
                      v-show="info.show"
                      :key="name"
                      :disabled="isDisableAllInput"
                      :labelI18nKey="info.labelI18nKey"
                      :optionList="info.optionList"
                    />
                  </div>
                </div>
              </div>
              <div class="cost-percent-slider">
                <div class="slider-wrapper">
                  <MutilRangeSlider
                    v-for="(item, name) in costPercentSlider"
                    v-show="item.show"
                    v-model="item.value"
                    :key="name"
                    :step="1"
                    :range-config="item.config"
                    :disabled="isDisableAllInput || item.disabled"
                  />
                </div>
                <div class="user-mode-btn" @click="handleUserModeBtnClick">
                  <i class="el-icon-sort"></i>
                </div>
              </div>
            </div>
          </div>
          <div class="editor-line cost-detail">
            <div v-show="activeStepNames.length > 0" class="cost-card-container" :style="costCardContainerStyle">
              <div class="detail-card detail-label">
                <div class="detail-row-list label-list">
                  <div class="detail-row"></div>
                  <div class="detail-row">
                    <div class="self-detail-row">
                      <label>{{ $t(`${i18nKeyPrefix}.label.referrer.root`) }}</label>
                      <el-popover placement="top" trigger="hover" popper-class="referral-chain-tooltip">
                        <ChainTipCard referrer-type="root"></ChainTipCard>
                        <template #reference>
                          <van-icon name="question-o" class="referral-tip-icon" />
                        </template>
                      </el-popover>
                    </div>
                    <template v-if="!onlyEmployee">
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.employee`) }}</label>
                      </div>
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.notEmployee`) }}</label>
                      </div>
                    </template>
                  </div>
                  <div class="detail-row">
                    <div class="self-detail-row">
                      <label>{{ $t(`${i18nKeyPrefix}.label.referrer.middle`) }}</label>
                      <el-popover placement="top" trigger="hover" popper-class="referral-chain-tooltip">
                        <ChainTipCard referrer-type="middle"></ChainTipCard>
                        <template #reference>
                          <van-icon name="question-o" class="referral-tip-icon" />
                        </template>
                      </el-popover>
                    </div>
                    <template v-if="!onlyEmployee">
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.employee`) }}</label>
                      </div>
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.notEmployee`) }}</label>
                      </div>
                    </template>
                  </div>
                  <div class="detail-row">
                    <div class="self-detail-row">
                      <label>{{ $t(`${i18nKeyPrefix}.label.referrer.direct`) }}</label>
                      <el-popover placement="top" trigger="hover" popper-class="referral-chain-tooltip">
                        <ChainTipCard referrer-type="direct"></ChainTipCard>
                        <template #reference>
                          <van-icon name="question-o" class="referral-tip-icon" />
                        </template>
                      </el-popover>
                    </div>
                    <template v-if="!onlyEmployee">
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.employee`) }}</label>
                      </div>
                      <div class="child-detail-row">
                        <label>{{ $t(`${i18nKeyPrefix}.label.hireType.notEmployee`) }}</label>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
              <div class="group-card-container">
                <template v-for="(group, groupName) in groupedHireStepCostInfo">
                  <div
                    v-if="group && !group.hidden"
                    class="detail-card step-group-card"
                    :key="groupName"
                  >
                    <div class="group-card" :class="group.classList">
                      <template v-for="(stepInfo, stepName) in group.children">
                        <div
                          v-if="stepInfo && !stepInfo.hidden"
                          class="step-card"
                          :key="stepName"
                          :class="stepName"
                        >
                          <div class="step-color-bar" :style="stepInfo.colorBarStyle"></div>
                          <div class="step-cost">
                            <div class="cost-percent" :class="isUserMode && 'hidden'">
                              <span v-if="stepInfo.percent.cash" class="cash">{{ `${stepInfo.percent.cash}%` }} </span>
                              <span v-if="showPointsReferralConfig && stepInfo.percent.point" class="point"> {{ `${stepInfo.percent.point}%` }}</span>
                            </div>
                            <div class="cost-number">
                              <span class="cash">{{ $t(`${i18nKeyPrefix}.symbols.cash`) }} {{ (stepInfo.cost.cash || 0)|formatBigNumber }}</span>
                              <span v-if="showPointsReferralConfig" class="point">{{ $t(`${i18nKeyPrefix}.symbols.point`) }} {{ (stepInfo.cost.point || 0)|formatBigNumber }}</span>
                            </div>
                          </div>
                          <div class="detail-row-list cost-list" :style="costListStyle[stepName]">
                            <div class="detail-row step-name">
                              <label>{{ $t(stepInfo.labelI18nKey) }}</label>
                            </div>
                            <div v-for="(referrerInfo, referrerType) in stepInfo.referrer" class="detail-row" :key="referrerType">
                              <CostInput
                                class="self-detail-row referral-cost-input"
                                :class="{ 'limit-width': Object.values(groupedHireStepCostInfo).filter(item => !item.hidden).length < 2 }"
                                :disabled="isDisableAllInput"
                                :ref="`${stepName}_${referrerType}_self`"
                                :config="referrerInfo.self">
                              </CostInput>
                              <CostInput
                                v-for="(childInfo, childName) in referrerInfo.children"
                                class="child-detail-row"
                                :class="{ 'limit-width': Object.values(groupedHireStepCostInfo).filter(item => !item.hidden).length < 2 }"
                                :disabled="isDisableAllInput"
                                :key="childName"
                                :ref="`${stepName}_${referrerType}_child_${childName}`"
                                :config="childInfo"
                              >
                              </CostInput>
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div class="step-group-cost">
                      <label>{{ $t(group.cost.labelI18nKey) }}: </label>
                      <span class="cash">{{ $t(`${i18nKeyPrefix}.symbols.cash`) }} {{ group.cost.cash|formatBigNumber }}</span>
                      <span v-if="showPointsReferralConfig" class="point">{{ $t(`${i18nKeyPrefix}.symbols.point`) }} {{ group.cost.point|formatBigNumber }}</span>
                      <el-tooltip v-if="group.cost.tipI18nKey" effect="dark" placement="top" popper-class="referral-tip-popper" class="referral-tip-icon">
                        <template #content>
                          <span v-html="$t(group.cost.tipI18nKey)"></span>
                        </template>
                        <van-icon name="question-o" />
                      </el-tooltip>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
        <div class="referral-total-cost-container">
          <div class="plan-cost">
            <div class="label">{{ $t(`${i18nKeyPrefix}.label.planCost`) }}</div>
            <div>{{ $t(`${i18nKeyPrefix}.symbols.cash`) }} {{ totalCost.cash|formatBigNumber }}</div>
            <div v-if="showPointsReferralConfig">{{ $t(`${i18nKeyPrefix}.symbols.point`) }} {{ totalCost.point|formatBigNumber }}</div>
          </div>
          <div class="actual-cost">
            <div class="label">{{ $t(`${i18nKeyPrefix}.label.actualCost`) }}</div>
            <div>{{ $t(`${i18nKeyPrefix}.symbols.cash`) }} {{ totalActualCost.cash|formatBigNumber }}</div>
            <div v-if="showPointsReferralConfig">{{ $t(`${i18nKeyPrefix}.symbols.point`) }} {{ totalActualCost.point|formatBigNumber }}</div>
          </div>
        </div>
        <div class="bottom-btns adapt-wrapper">
          <el-button class="cancel-btn grey-btn" @click="handleCancel">
            <template v-if="[CONFIG_STATUS.pending, CONFIG_STATUS.archived].includes(configStatus)">
              {{ $t('common.btn.back') }}
            </template>
            <template v-else>
              {{ $t('common.btn.cancel') }}
            </template>
          </el-button>
          <template v-if="![CONFIG_STATUS.pending, CONFIG_STATUS.archived].includes(configStatus)">
            <el-button v-if="CONFIG_STATUS.approved !== configStatus" class="save-draft-btn blue-btn" @click="handleSaveDraft">{{ $t(`${i18nKeyPrefix}.btn.saveDraft`) }}</el-button>
            <el-button class="save-btn blue-btn" @click="handleSave">{{ $t(`${i18nKeyPrefix}.btn.save`) }}</el-button>
          </template>
        </div>
      </div>
    </template>
  </AsideLayout>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import AsideLayout from '../../layout/asideLayout'
import RectSwitch from '@/components/switch/rectSwitch'
import MutilRangeSlider from './mutilRangeSlider'
import CostInput from './costInput'
import ReferralInput from './referralInput'
import ReferralTypeSelect from './referralTypeSelect'
import NumberInput from './numberInput'
import ChainTipCard from './referralChianTipCard'

const REWARD_TYPE = {
  cash: 'cash',
  point: 'points',
  payroll: 'payroll'
}

const COST_TYPE = {
  cash: 'cash',
  point: 'point'
}

const HIRE_STEP = {
  probation: 'probation',
  onboard: 'onboard',
  interview: 'interview',
  apply: 'apply'
}

const GROUP_NAMES = [
  [HIRE_STEP.probation, HIRE_STEP.onboard],
  [HIRE_STEP.interview, HIRE_STEP.apply]
]

const HIRE_TYPE = {
  employee: 'ee',
  notEmployee: 'non_ee'
}

const REFERRER_TYPE = {
  root: 'root',
  middle: 'middle',
  direct: 'direct'
}

const EDIT_STATUS = {
  allDisabled: 'allDisabled',
  onlyHireCount: 'onlyHireCount',
  allEnabled: 'allEnabled'
}

const CONFIG_STATUS = {
  draft: '1',
  pending: '2',
  approved: '3',
  rejected: '6',
  archived: '7'
}

function getConfigStatusName (configStatus) {
  if (!configStatus) {
    return null
  }
  let statusName = null
  _.forEach(CONFIG_STATUS, (status, name) => {
    if (status === configStatus) {
      statusName = name
    }
    return !statusName
  })
  return statusName
}

export default {
  components: {
    AsideLayout, RectSwitch, MutilRangeSlider, CostInput, ReferralInput, ReferralTypeSelect, NumberInput, ChainTipCard
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      EDIT_STATUS,
      CONFIG_STATUS,
      getConfigStatusName,
      i18nKeyPrefix: 'admin.job.referral',
      saving: false,
      loading: false,
      disableUserModeBtn: false,
      isUserMode: false,
      editStatus: EDIT_STATUS.allEnabled,
      configStatus: null,
      stepColor: {
        probation: '#0096cc',
        onboard: '#66b351',
        interview: '#ffd334',
        apply: '#f57c23'
      },
      stepStatus: {
        probation: false,
        onboard: false,
        interview: false,
        apply: false
      },
      stepPercent: {
        cash: {
          probation: 0,
          onboard: 0,
          interview: 0,
          apply: 0
        },
        point: {
          probation: 0,
          onboard: 0,
          interview: 0,
          apply: 0
        }
      },
      costPercentSlider: {
        cash: { show: true, value: {}, _value: {}, config: {}, disabled: false },
        point: { show: true, value: {}, _value: {}, config: {}, disabled: false }
      },
      stepChooseInfo: [],
      hireStepCostInfo: {},
      stepCostErrors: [],
      historyInfo: [],
      referralInfo: {
        orginReferralData: { job: { company: null }, is_new: false },
        hireNumber: 1,
        onlyEmployee: false,
        everyHireCost: {
          cash: 0,
          point: 0
        },
        hireStepConfig: {
          probation: {
            plan: { is_enable: false, cash: 0, point: 0 },
            actual: { cash: 0, point: 0 },
            ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              root: { count: 0, amount: 0 },
              middle: { count: 0, amount: 0 },
              direct: { count: 0, amount: 0 }
            },
            non_ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              root: { count: 0, amount: 0 },
              middle: { count: 0, amount: 0 },
              direct: { count: 0, amount: 0 }
            }
          },
          onboard: {
            plan: { is_enable: false, cash: 0, point: 0 },
            actual: { cash: 0, point: 0 },
            ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              root: { count: 0, amount: 0 },
              middle: { count: 0, amount: 0 },
              direct: { count: 0, amount: 0 }
            },
            non_ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cashactual
                }
                return null
              },
              root: { count: 0, amount: 0 },
              middle: { count: 0, amount: 0 },
              direct: { count: 0, amount: 0 }
            }
          },
          interview: {
            plan: { is_enable: false, cash: 0, point: 0 },
            actual: { cash: 0, point: 0 },
            ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              direct: { count: 0, amount: 0 }
            },
            non_ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              direct: { count: 0, amount: 0 }
            }
          },
          apply: {
            plan: { is_enable: false, cash: 0, point: 0 },
            actual: { cash: 0, point: 0 },
            ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              direct: { count: 0, amount: 0 }
            },
            non_ee: {
              type: REWARD_TYPE.cash,
              get costType () {
                if (!this.type) {
                  return null
                }
                if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                  return COST_TYPE.cash
                } else {
                  return COST_TYPE.point
                }
              },
              direct: { count: 0, amount: 0 }
            }
          }
        }
      }
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    jobId () {
      return this.$route.params.jobId
    },
    jobInfo () {
      return this.referralInfo.orginReferralData.job
    },
    jobName () {
      const jobInfo = this.jobInfo || {}
      return this.isEn ? jobInfo.title : jobInfo.cn_title
    },
    pageTitle () {
      return this.$t(`${this.i18nKeyPrefix}.title`, { jobName: this.jobName })
    },
    isLiteReferralConfig () {
      return this.jobInfo.company?.is_lite_referral
    },
    showPointsReferralConfig () {
      if (!this.isLiteReferralConfig && this.jobInfo.company?.is_referral_points_rewards_enable) {
        return true
      }
      return this.totalCost.point > 0 && (this.isDisableAllInput || this.isOnlyEnableHireNumberInput)
    },
    configStatusName () {
      return getConfigStatusName(this.configStatus)
    },
    isDisableAllInput () {
      return this.editStatus !== EDIT_STATUS.allEnabled
    },
    isOnlyEnableHireNumberInput () {
      return this.editStatus === EDIT_STATUS.onlyHireCount
    },
    minHireNumber () {
      return (this.isOnlyEnableHireNumberInput && this.referralInfo.orginReferralData?.setting?.hire_count) || 1
    },
    hireStepConfig () {
      return this.referralInfo.hireStepConfig
    },
    onlyEmployee () {
      return this.referralInfo.onlyEmployee
    },
    activeStepNames () {
      const names = []
      _.forEach(this.stepStatus, (value, name) => {
        if (value) {
          names.push(name)
        }
      })
      return names
    },
    costTypeActiveStepNames () {
      const costTypeNames = { [COST_TYPE.cash]: [], [COST_TYPE.point]: [] }
      _.forEach(COST_TYPE, costType => {
        _.forEach(this.activeStepNames, (stepName) => {
          const stepConfig = this.hireStepConfig[stepName]
          const hireTypes = [HIRE_TYPE.employee]
          if (!this.onlyEmployee) {
            hireTypes.push(HIRE_TYPE.notEmployee)
          }
          const activeHireType = _.find(hireTypes, (hireType) => {
            return stepConfig[hireType].costType === costType
          })
          if (activeHireType) {
            costTypeNames[costType].push(stepName)
          }
        })
      })
      return costTypeNames
    },
    totalCost () {
      const referralInfo = this.referralInfo
      return {
        cash: referralInfo.everyHireCost.cash * referralInfo.hireNumber,
        point: referralInfo.everyHireCost.point * referralInfo.hireNumber
      }
    },
    totalActualCost () {
      const totalActualCost = { [COST_TYPE.cash]: 0, [COST_TYPE.point]: 0 }
      _.forEach(HIRE_STEP, stepName => {
        const stepInfo = this.hireStepCostInfo[stepName]
        totalActualCost.cash += stepInfo?.actualCost.total.cash || 0
        totalActualCost.point += stepInfo?.actualCost.total.point || 0
      })
      return totalActualCost
    },
    groupedHireStepCostInfo () {
      const vm = this
      return {
        probationAndOnboard: {
          get hidden () {
            const childern = this.children
            return childern?.probation?.hidden && childern?.onboard?.hidden
          },
          classList: [],
          cost: {
            labelI18nKey: `${this.i18nKeyPrefix}.label.postpaid`,
            tipI18nKey: `${this.i18nKeyPrefix}.message.referralTip.postpaid`,
            get cash () {
              let cost = 0
              _.forEach(GROUP_NAMES[0], (stepName) => {
                const stepInfo = vm.hireStepCostInfo[stepName]
                cost += stepInfo?.actualCost.total.cash
              })
              return cost || 0
            },
            get point () {
              let cost = 0
              _.forEach(GROUP_NAMES[0], (stepName) => {
                const stepInfo = vm.hireStepCostInfo[stepName]
                cost += stepInfo?.actualCost.total.point
              })
              return cost || 0
            }
          },
          children: {
            probation: this.hireStepCostInfo.probation,
            onboard: this.hireStepCostInfo.onboard
          }
        },
        interviewAndApply: {
          get hidden () {
            const childern = this.children
            return childern?.apply?.hidden && childern?.interview?.hidden
          },
          classList: [],
          cost: {
            labelI18nKey: `${this.i18nKeyPrefix}.label.prepay`,
            tipI18nKey: `${this.i18nKeyPrefix}.message.referralTip.prepay`,
            get cash () {
              let cost = 0
              _.forEach(GROUP_NAMES[1], (stepName) => {
                const stepInfo = vm.hireStepCostInfo[stepName]
                cost += stepInfo?.actualCost.total.cash
              })
              return cost || 0
            },
            get point () {
              let cost = 0
              _.forEach(GROUP_NAMES[1], (stepName) => {
                const stepInfo = vm.hireStepCostInfo[stepName]
                cost += stepInfo?.actualCost.total.point
              })
              return cost || 0
            }
          },
          children: {
            interview: this.hireStepCostInfo.interview,
            apply: this.hireStepCostInfo.apply
          }
        }
      }
    },
    costCardContainerStyle () {
      const group = this.groupedHireStepCostInfo
      return {
        flex: (group.probationAndOnboard.hidden || group.interviewAndApply.hidden) ? null : 1
      }
    },
    costListStyle () {
      const style = {}
      const staticStyle = {
        maxWidth: '50%'
      }
      const stepStatus = this.stepStatus
      if (!stepStatus.probation || !stepStatus.onboard) {
        style.probation = style.onboard = staticStyle
      }
      if (!stepStatus.apply || !stepStatus.interview) {
        style.apply = style.interview = staticStyle
      }
      return style
    }
  },
  created () {
    this.initReferralInfo()
  },
  methods: {
    ...mapActions('jobReferralManagement', ['getJobReferralInfo', 'submitJobReferral', 'saveDraftJobReferral']),
    async initReferralInfo () {
      this.$eventBus.$emit('showLoading', true)
      try {
        this.loading = true
        const resp = await this.getJobReferralInfo({ jobId: this.jobId })
        if (resp) {
          const planCost = { cash: 0, point: 0 }
          if (!resp.is_new) {
            const referralInfo = {}
            const settingData = resp.setting
            const hireNumber = settingData.hire_count
            referralInfo.hireNumber = hireNumber
            referralInfo.everyHireCost = {
              cash: settingData.budget,
              point: settingData.points_budget
            }
            referralInfo.onlyEmployee = !settingData.public
            const stepsSetting = JSON.parse(settingData.steps_setting)
            const hireStepConfig = {}

            _.forEach(stepsSetting, (setting, stepName) => {
              if (stepName === 'applied') {
                stepName = HIRE_STEP.apply
              }
              if (stepName === 'interviewed') {
                stepName = HIRE_STEP.interview
              }
              _.forEach(HIRE_TYPE, (hireType) => {
                const hireSetting = setting[hireType]
                if (GROUP_NAMES[1].includes(stepName)) {
                  hireSetting[REFERRER_TYPE.direct] = {
                    amount: hireSetting.amount,
                    count: hireSetting.count
                  }
                }
              })
              setting.plan = {
                is_enable: setting.plan.is_enable,
                cash: setting.plan.cash,
                point: setting.plan.points
              }
              planCost.cash += setting.plan.cash
              planCost.point += setting.plan.points
              hireStepConfig[stepName] = setting
            })
            referralInfo.hireStepConfig = hireStepConfig
            this.referralInfo = referralInfo
            this.configStatus = settingData.status
            this.historyInfo = settingData.history_info
          } else {
            this.configStatus = null
          }
          switch (this.configStatus) {
            case CONFIG_STATUS.approved:
              this.editStatus = EDIT_STATUS.onlyHireCount
              break

            case null:
            case CONFIG_STATUS.draft:
            case CONFIG_STATUS.rejected:
              this.editStatus = EDIT_STATUS.allEnabled
              break

            default:
              this.editStatus = EDIT_STATUS.allDisabled
              break
          }
          this.referralInfo.orginReferralData = resp
          if (this.isLiteReferralConfig) {
            this.referralInfo.onlyEmployee = true
          }
          this.initDataReactive()
          if (!resp.is_new) {
            this.initStepActualCost()
          }
          this.refreshStepChooseInfo()
          this.refreshCostPercentSlider()
          this.refreshStepCostInfo()
          this.resetUserMode()
          if (this.configStatus === CONFIG_STATUS.approved) {
            if (planCost.cash !== this.totalCost.cash || planCost.point !== this.totalCost.point) {
              this.isUserMode = true
              this.disableUserModeBtn = true
            }
          }
        }
      } finally {
        this.$nextTick(() => { this.loading = false })
        this.$eventBus.$emit('showLoading', false)
      }
    },
    initDataReactive () {
      const vm = this
      const stepStatus = {}
      const stepPercent = {}
      _.forEach(HIRE_STEP, (stepName) => {
        const stepConfig = this.hireStepConfig[stepName]
        _.forEach(HIRE_TYPE, (hireType) => {
          const hireConfig = stepConfig[hireType]
          Object.defineProperty(hireConfig, 'costType', {
            enumerable: true,
            get () {
              if (!this.type) {
                return null
              }
              if ([REWARD_TYPE.cash, REWARD_TYPE.payroll].includes(this.type)) {
                return COST_TYPE.cash
              } else {
                return COST_TYPE.point
              }
            }
          })
        })
        const plan = stepConfig.plan
        _.forEach(COST_TYPE, costType => {
          if (!stepPercent.hasOwnProperty(costType)) {
            stepPercent[costType] = {}
          }
          const costPercent = stepPercent[costType]
          Object.defineProperty(costPercent, stepName, {
            enumerable: true,
            get () {
              const totalCost = vm.totalCost[costType]
              let percent = 0
              if (totalCost) {
                percent = 100 * plan[costType] / totalCost
              } else {
                percent = vm.costPercentSlider[costType]._value[stepName]
              }
              return Math.round(percent || 0)
            },
            set (val) {
              val = Math.round(val)
              const cost = vm.totalCost[costType] * val / 100
              plan[costType] = cost
            }
          })
        })
        Object.defineProperty(stepStatus, stepName, {
          enumerable: true,
          configurable: true,
          get () {
            return plan.is_enable
          },
          set (val) {
            plan.is_enable = val
          }
        })
      })
      this.stepStatus = stepStatus
      this.stepPercent = stepPercent
    },
    initStepActualCost () {
      const hireNumber = this.referralInfo.hireNumber
      _.forEach(HIRE_STEP, (stepName) => {
        const stepConfig = this.hireStepConfig[stepName]
        const referrerCostMap = { [COST_TYPE.cash]: {}, [COST_TYPE.point]: {} }
        _.forEach(HIRE_TYPE, (hireType) => {
          const hireConfig = stepConfig[hireType]
          const costType = hireConfig.costType
          const referrerCost = referrerCostMap[costType]
          _.forEach(REFERRER_TYPE, (referrerType) => {
            const referrerSetting = hireConfig[referrerType]
            if (!referrerSetting) {
              return
            }
            let cost = referrerSetting.amount
            if (GROUP_NAMES[0].includes(stepName)) {
              cost *= hireNumber
            } else {
              cost *= referrerSetting.count
            }
            if (referrerType === REFERRER_TYPE.middle) {
              cost *= referrerSetting.count
            }
            const maxCost = referrerCost[referrerType] || 0
            if (cost > maxCost) {
              referrerCost[referrerType] = cost
            }
          })
        })
        const actualCost = { [COST_TYPE.cash]: 0, [COST_TYPE.point]: 0 }
        _.forEach(referrerCostMap, (referrerCost, costType) => {
          _.forEach(referrerCost, (cost, referrerType) => {
            actualCost[costType] += cost
          })
        })
        stepConfig.actual = actualCost
      })
    },
    setReferrerConfigValue (newVal, isCost, stepInfo, referrerInfo, hireType, isAuto, noShowError) {
      let hasErrorr = false

      const stepName = stepInfo.name
      const referrerType = referrerInfo.name
      const stepConfig = stepInfo.stepConfig
      const hireConfig = referrerInfo.hireConfig[hireType]
      const costType = hireConfig.costType
      const stepActualCost = stepInfo.actualCost.total[costType]
      const referrerConfig = hireConfig[referrerType]
      const actualCost = referrerInfo.actualCost[costType]
      const isUserMode = this.isUserMode
      const hireNumber = this.referralInfo.hireNumber
      let stepPlanCost = stepInfo.cost[costType]

      if (isUserMode) {
        stepPlanCost = this.totalCost[costType]
        _.forEach(HIRE_STEP, hireStepName => {
          if (hireStepName !== stepName) {
            stepPlanCost -= this.hireStepCostInfo[hireStepName].actualCost.total[costType]
          }
        })
      }

      const totalOtherActualCost = stepActualCost - actualCost
      const maxPlanCost = stepPlanCost - totalOtherActualCost
      const hasHireNumber = GROUP_NAMES[0].includes(stepName)

      if (isCost && newVal === 1) {
        hasErrorr = true
      } else if (referrerType === REFERRER_TYPE.middle || GROUP_NAMES[1].includes(stepName)) {
        let newAmount = referrerConfig.amount
        let newCount = newVal
        if (!isUserMode) {
          newAmount = maxPlanCost / newVal
          if (hasHireNumber) {
            newAmount /= hireNumber
          }
          newAmount = parseInt(newAmount)
          if (isNaN(newAmount) || newAmount < 2) {
            newAmount = 0
          }
        } else if (isCost) {
          newCount = referrerConfig.count
          newAmount = newVal
        }
        let newCost = newCount * newAmount
        if (hasHireNumber) {
          newCost *= hireNumber
        }
        if (newCost > maxPlanCost) {
          hasErrorr = true
        }
        referrerConfig.count = newCount
        referrerConfig.amount = newAmount

        if (!isAuto && !isCost) {
          const anotherHireType = hireType === HIRE_TYPE.employee ? HIRE_TYPE.notEmployee : HIRE_TYPE.employee
          this.setReferrerConfigValue(newCount, false, stepInfo, referrerInfo, anotherHireType, true, noShowError)
        }
      } else {
        const middleReferrerInfo = stepInfo.referrer[REFERRER_TYPE.middle]
        const middleReferrerConfig = stepConfig[hireType][REFERRER_TYPE.middle]
        let newCost = newVal
        if (hasHireNumber) {
          newCost *= hireNumber
        }
        referrerConfig.amount = newVal
        if (newCost > maxPlanCost) {
          if (isUserMode) {
            hasErrorr = true
          } else {
            const newMaxPlanCost = maxPlanCost + middleReferrerInfo.actualCost[costType]
            if (newCost > newMaxPlanCost) {
              hasErrorr = true
            } else if (newMaxPlanCost !== maxPlanCost) {
              this.setReferrerConfigValue(middleReferrerConfig.count, false, stepInfo, middleReferrerInfo, hireType, false, noShowError)
            }
          }
        } else {
          if (!isUserMode) {
            this.setReferrerConfigValue(middleReferrerConfig.count, false, stepInfo, middleReferrerInfo, hireType, false, noShowError)
          }
        }
      }

      if (hasErrorr) {
        if (!noShowError) {
          this.showError({ newVal, isCost, stepInfo, referrerInfo, hireType, isAuto })
        }
      } else {
        stepConfig.actual[costType] = stepInfo.actualCost.total[costType]
        if (isUserMode) {
          stepConfig.plan[costType] = stepConfig.actual[costType]
        }
        this.clearError({ newVal, isCost, stepInfo, referrerInfo, hireType, isAuto })
        if (!noShowError && !isAuto) {
          this.resetErrors()
        }
      }
      return hasErrorr
    },
    getStepDefaultPercent (costType, stepName) {
      const percent = {
        probation: 0,
        onboard: 0,
        interview: 0,
        apply: 0
      }
      const costTypeActiveStepNames = this.costTypeActiveStepNames[costType]
      if (costTypeActiveStepNames.length === 1) {
        percent[costTypeActiveStepNames[0]] = 100
      } else {
        const probation = costTypeActiveStepNames.includes(HIRE_STEP.probation)
        const onboard = costTypeActiveStepNames.includes(HIRE_STEP.onboard)
        const interview = costTypeActiveStepNames.includes(HIRE_STEP.interview)
        const apply = costTypeActiveStepNames.includes(HIRE_STEP.apply)

        const hasTwoGroup = (probation || onboard) && (interview || apply)
        if (hasTwoGroup) {
          let remain = 100
          if (interview) {
            percent.interview = 10
            remain -= 10
          }
          if (apply) {
            percent.apply = 10
            remain -= 10
          }
          if (probation) {
            percent.probation = remain
            remain = 0
          }
          if (onboard) {
            if (remain) {
              percent.onboard = remain
            } else {
              percent.probation = percent.onboard = percent.probation / 2
            }
          }
        } else {
          _.forEach(costTypeActiveStepNames, (name) => {
            percent[name] = 50
          })
        }
      }
      return stepName ? percent[stepName] : percent
    },
    refreshCostPercentSlider (costType, defaultValue) {
      const vm = this
      if (!costType) {
        _.forEach(COST_TYPE, type => this.refreshCostPercentSlider(type))
        const virtualSlider = {
          get show () {
            const activeNames = vm.activeStepNames
            return activeNames.length < 1 || vm.isUserMode
          },
          value: {},
          _value: {},
          config: {},
          get disabled () {
            const activeNames = vm.activeStepNames
            return activeNames.length > 0
          }
        }
        _.forEach(HIRE_STEP, stepName => {
          virtualSlider.config[stepName] = {
            get show () {
              const activeNames = vm.activeStepNames
              return activeNames.includes(stepName)
            },
            get color () {
              return vm.stepColor[stepName]
            }
          }
          Object.defineProperty(virtualSlider.value, stepName, {
            enumerable: true,
            get () {
              const activeNames = vm.activeStepNames
              if (activeNames.includes(stepName)) {
                return 100 / activeNames.length
              } else {
                return 0
              }
            }
          })
        })
        this.costPercentSlider.virtual = virtualSlider
        return
      }
      if (!defaultValue) {
        defaultValue = {}
        if (this.totalCost[costType] === 0) {
          const defaultPercent = this.getStepDefaultPercent(costType)
          _.forEach(defaultPercent, (percent, stepName) => {
            defaultValue[stepName] = percent
          })
        } else {
          const totalCost = this.totalCost[costType]
          _.forEach(HIRE_STEP, (stepName) => {
            const stepConfig = this.hireStepConfig[stepName]
            const percent = 100 * stepConfig.plan[costType] / totalCost
            defaultValue[stepName] = Math.round(percent || 0)
          })
        }
      }
      const slider = { get show () { return !this.disabled }, value: {}, _value: defaultValue, config: {}, disabled: false }
      _.forEach(HIRE_STEP, (stepName) => {
        Object.defineProperty(slider.value, stepName, {
          enumerable: true,
          get () {
            return slider._value[stepName] || 0
          },
          set (val) {
            slider._value[stepName] = val
            vm.stepPercent[costType][stepName] = val
            vm.resetStepCost(stepName, costType)
          }
        })
        slider.config[stepName] = {
          get show () {
            const activeNames = vm.costTypeActiveStepNames[costType]
            return activeNames.includes(stepName)
          },
          get color () {
            return vm.stepColor[stepName]
          }
        }
      })
      Object.defineProperty(slider, 'disabled', {
        enumerable: true,
        configurable: true,
        get () {
          const activeNames = vm.costTypeActiveStepNames[costType]
          const activeStepNames = vm.activeStepNames
          return activeStepNames.length < 1 || vm.isUserMode || activeNames.length !== activeStepNames.length
        }
      })
      this.costPercentSlider[costType] = slider
    },
    refreshStepChooseInfo () {
      const optionList = [{ value: REWARD_TYPE.cash, labelI18nKey: `${this.i18nKeyPrefix}.label.referralType.cash` }]
      if (this.showPointsReferralConfig) {
        optionList.push({ value: REWARD_TYPE.point, labelI18nKey: `${this.i18nKeyPrefix}.label.referralType.point` })
      }
      const vm = this
      const groupInfo = []
      _.forEach(GROUP_NAMES, (names, index) => {
        const info = { names, choose: {}, select: {} }
        groupInfo.push(info)
        _.forEach(names, (stepName) => {
          info.choose[stepName] = {
            labelI18nKey: `${this.i18nKeyPrefix}.label.hireStep.${stepName}`,
            tipI18nKey: `${this.i18nKeyPrefix}.message.referralTip.hireStep.${stepName}`,
            color: this.stepColor[stepName],
            get value () {
              return vm.stepStatus[stepName]
            },
            set value (val) {
              vm.stepStatus[stepName] = val
            }
          }
        })
        if (this.isLiteReferralConfig) {
          return
        }
        _.forEach(HIRE_TYPE, (hireType, hireTypeName) => {
          const selectInfo = {
            hireType,
            labelI18nKey: `${this.i18nKeyPrefix}.label.hireType.${hireTypeName}`,
            optionList,
            get show () {
              return hireType === HIRE_TYPE.employee || !vm.onlyEmployee
            },
            get value () {
              const stepConfig = vm.hireStepConfig[info.names[0]]
              return stepConfig[this.hireType].type
            },
            set value (val) {
              _.forEach(info.names, name => {
                const stepConfig = vm.hireStepConfig[name]
                const hireConfig = stepConfig[this.hireType]
                hireConfig.type = val
              })
            }
          }
          if (hireType === HIRE_TYPE.employee && !this.isLiteReferralConfig) {
            selectInfo.optionList = [
              ...optionList,
              { value: REWARD_TYPE.payroll, labelI18nKey: `${this.i18nKeyPrefix}.label.referralType.payroll` }
            ]
          }
          info.select[hireType] = selectInfo
        })
      })
      this.stepChooseInfo = groupInfo
    },
    refreshStepCostInfo () {
      const info = {}
      const vm = this
      _.forEach(HIRE_STEP, (stepName) => {
        const stepConfig = this.hireStepConfig[stepName]
        const stepInfo = {
          name: stepName,
          stepConfig,
          labelI18nKey: `${this.i18nKeyPrefix}.label.hireStep.${stepName}`,
          colorBarStyle: { backgroundColor: this.stepColor[stepName] },
          get hidden () {
            return !stepConfig.plan.is_enable
          },
          percent: {
            get cash () {
              return vm.stepPercent[COST_TYPE.cash][stepName]
            },
            get point () {
              return vm.stepPercent[COST_TYPE.point][stepName]
            }
          },
          cost: {
            get cash () {
              return stepConfig.plan[COST_TYPE.cash]
            },
            get point () {
              return stepConfig.plan[COST_TYPE.point]
            }
          },
          referrer: {},
          actualCost: {
            total: 0
          }
        }
        _.forEach(REFERRER_TYPE, (referrerType) => {
          const referrerInfo = {
            name: referrerType,
            hireConfig: {},
            costInfo: {},
            get self () {
              let self = {}
              if (vm.onlyEmployee) {
                self = this.costInfo[HIRE_TYPE.employee]
              }
              return self
            },
            get children () {
              const children = {}
              if (!vm.onlyEmployee) {
                children[HIRE_TYPE.employee] = this.costInfo[HIRE_TYPE.employee]
                children[HIRE_TYPE.notEmployee] = this.costInfo[HIRE_TYPE.notEmployee]
              }
              return children
            },
            get actualCost () {
              const actualCost = { [COST_TYPE.cash]: 0, [COST_TYPE.point]: 0 }
              const hireTypes = [HIRE_TYPE.employee]
              if (!vm.onlyEmployee) {
                hireTypes.push(HIRE_TYPE.notEmployee)
              }
              _.forEach(hireTypes, hireType => {
                const hireConfig = this.hireConfig[hireType]
                const referrerConfig = hireConfig[referrerType]
                if (!referrerConfig) {
                  return
                }
                const costType = hireConfig.costType
                let cost = referrerConfig.amount
                if (GROUP_NAMES[0].includes(stepName)) {
                  cost *= vm.referralInfo.hireNumber
                }
                if (GROUP_NAMES[1].includes(stepName) || referrerType === REFERRER_TYPE.middle) {
                  cost *= referrerConfig.count
                }
                const maxCost = actualCost[costType] || 0
                if (cost > maxCost) {
                  actualCost[costType] = cost
                }
              })
              return actualCost
            }
          }
          _.forEach(HIRE_TYPE, (hireType) => {
            const hireConfig = stepConfig[hireType]
            referrerInfo.hireConfig[hireType] = hireConfig
            let costInfo = null
            if (hireConfig[referrerType]) {
              if (GROUP_NAMES[0].includes(stepName)) {
                costInfo = {
                  hire: { get value () { return vm.referralInfo.hireNumber || 0 } },
                  cost: {
                    input: true,
                    inputProp: { get type () { return hireConfig.costType } },
                    get value () { return hireConfig[referrerType].amount },
                    set value (val) {
                      vm.setReferrerConfigValue(val, true, stepInfo, referrerInfo, hireType)
                    }
                  }
                }
              } else {
                costInfo = {
                  hire: {
                    input: true,
                    inputProp: {
                      customIcon: { placement: 'prefix', class: 'iconfont iconren' }
                    },
                    get value () { return hireConfig[referrerType].count },
                    set value (val) {
                      vm.setReferrerConfigValue(val, false, stepInfo, referrerInfo, hireType)
                    }
                  },
                  cost: {
                    get input () { return vm.isUserMode },
                    inputProp: { get type () { return hireConfig.costType } },
                    get value () { return hireConfig[referrerType].amount },
                    set value (val) {
                      vm.setReferrerConfigValue(val, true, stepInfo, referrerInfo, hireType)
                    }
                  }
                }
              }
            }
            if (costInfo && referrerType === REFERRER_TYPE.middle) {
              const middleReferrerInfo = {
                input: true,
                inputProp: {
                  customIcon: { placement: 'prefix', class: 'iconfont iconren' }
                },
                get value () { return hireConfig[referrerType].count },
                set value (val) {
                  vm.setReferrerConfigValue(val, false, stepInfo, referrerInfo, hireType)
                }
              }
              costInfo.cost = {
                get input () { return vm.isUserMode },
                inputProp: { get type () { return hireConfig.costType } },
                get value () { return hireConfig[referrerType].amount },
                set value (val) {
                  vm.setReferrerConfigValue(val, true, stepInfo, referrerInfo, hireType)
                }
              }
              costInfo = {
                hire: costInfo.hire,
                referrer: middleReferrerInfo,
                cost: costInfo.cost
              }
            }
            referrerInfo.costInfo[hireType] = costInfo || {}
          })
          stepInfo.referrer[referrerType] = referrerInfo
          Object.defineProperty(stepInfo.actualCost, referrerType, {
            enumerable: true,
            configurable: true,
            get () {
              return referrerInfo.actualCost
            }
          })
        })
        Object.defineProperty(stepInfo.actualCost, 'total', {
          enumerable: true,
          configurable: true,
          get () {
            const actualCost = { [COST_TYPE.cash]: 0, [COST_TYPE.point]: 0 }
            _.forEach(REFERRER_TYPE, (referrerType) => {
              const referrallActualCost = this[referrerType]
              _.forEach(COST_TYPE, costType => {
                actualCost[costType] += referrallActualCost[costType] || 0
              })
            })
            return actualCost
          }
        })

        info[stepName] = stepInfo
      })
      this.hireStepCostInfo = info
    },
    resetStepPercent (costType) {
      if (costType) {
        const defaultPercent = this.getStepDefaultPercent(costType)
        _.forEach(defaultPercent, (percent, stepName) => {
          this.costPercentSlider[costType].value[stepName] = percent
        })
      } else {
        _.forEach(COST_TYPE, costType => {
          this.resetStepPercent(costType)
        })
      }
    },
    resetStepCost (stepName, resetCostType) {
      const stepConfig = this.hireStepConfig[stepName]
      const hireNumber = this.referralInfo.hireNumber
      _.forEach(HIRE_TYPE, (hireType) => {
        const hireConfig = stepConfig[hireType]
        const costType = hireConfig.costType
        if (resetCostType && resetCostType !== costType) {
          return
        }
        _.forEach(REFERRER_TYPE, (referrerType) => {
          const referrerConfig = hireConfig[referrerType]
          if (!referrerConfig) {
            return
          }
          if (GROUP_NAMES[1].includes(stepName) || referrerType === REFERRER_TYPE.middle) {
            referrerConfig.count = 1
          } else {
            referrerConfig.count = hireNumber
          }
          if (referrerType === REFERRER_TYPE.direct) {
            let cost = stepConfig.plan[costType]
            if (GROUP_NAMES[0].includes(stepName)) {
              cost /= hireNumber
            }
            if (this.onlyEmployee && hireType === HIRE_TYPE.notEmployee) {
              referrerConfig.amount = 0
            } else {
              referrerConfig.amount = cost || 0
            }
          } else {
            referrerConfig.amount = 0
          }
        })
      })
      stepConfig.actual = {
        cash: stepConfig.plan.cash,
        point: stepConfig.plan.point
      }
      if (this.stepCostErrors.length > 0) {
        this.clearError()
      }
    },
    resetUserMode () {
      const hireTypes = this.referralInfo.onlyEmployee ? [HIRE_TYPE.employee] : Object.values(HIRE_TYPE)
      let hasDifferentCostType = false
      let lastType = null
      _.forEach(this.activeStepNames, (stepName) => {
        const stepConfig = this.hireStepConfig[stepName]
        _.forEach(hireTypes, (hireType) => {
          const hireConfig = stepConfig[hireType]
          const costType = hireConfig.costType
          if (lastType && lastType !== costType) {
            hasDifferentCostType = true
          }
          lastType = costType
          return !hasDifferentCostType
        })
        return !hasDifferentCostType
      })

      this.isUserMode = hasDifferentCostType
      this.disableUserModeBtn = hasDifferentCostType
    },
    getCostInputRefByErrorInfo (errorInfo) {
      const stepInfo = errorInfo.stepInfo
      const stepName = stepInfo.name
      const referrerInfo = errorInfo.referrerInfo
      const referrerType = referrerInfo.name
      const hireType = errorInfo.hireType
      const isCost = errorInfo.isCost
      let refName = null
      if (this.onlyEmployee) {
        refName = `${stepName}_${referrerType}_self`
      } else {
        refName = `${stepName}_${referrerType}_child_${hireType}`
      }
      let costInputRef = this.$refs[refName]
      if (costInputRef && costInputRef.hasOwnProperty('length')) {
        costInputRef = costInputRef[0]
      }
      let inputName = 'cost'
      if (isCost) {
        inputName = 'cost'
      } else if (referrerType === REFERRER_TYPE.middle) {
        inputName = 'referrer'
      } else {
        inputName = 'hire'
      }
      return { costInputRef, inputName }
    },
    getErrorName (errorInfo) {
      const stepInfo = errorInfo.stepInfo
      const stepName = stepInfo.name
      const referrerInfo = errorInfo.referrerInfo
      const referrerType = referrerInfo.name
      const hireType = errorInfo.hireType
      const isCost = errorInfo.isCost
      return `${stepName}_${referrerType}_${hireType}_${isCost ? 'cost' : 'count'}`
    },
    resetErrors () {
      const errors = [...this.stepCostErrors]
      _.forEach(errors, errorInfo => {
        const { newVal, isCost, stepInfo, referrerInfo, hireType, isAuto } = errorInfo.info
        this.setReferrerConfigValue(newVal, isCost, stepInfo, referrerInfo, hireType, isAuto, true)
      })
    },
    clearError (errorInfo) {
      if (errorInfo) {
        const { costInputRef, inputName } = this.getCostInputRefByErrorInfo(errorInfo)
        if (!costInputRef) {
          return
        }
        costInputRef.clearError(inputName)
        const errorName = this.getErrorName(errorInfo)
        this.stepCostErrors = _.filter(this.stepCostErrors, error => error.name !== errorName)
      } else {
        _.forEach(this.stepCostErrors, errorInfo => {
          this.clearError(errorInfo.info)
        })
        this.stepCostErrors = []
      }
    },
    showError (errorInfo) {
      let errorMsgI18nKey = ''
      if (errorInfo.isCost && errorInfo.newVal < 2) {
        errorMsgI18nKey = 'stepCostToolSmall'
      } else {
        errorMsgI18nKey = 'stepActualCostMoreThanPlan'
      }
      if (errorMsgI18nKey) {
        this.$message.error(this.$t(`${this.i18nKeyPrefix}.message.${errorMsgI18nKey}`))
      }
      const { costInputRef, inputName } = this.getCostInputRefByErrorInfo(errorInfo)
      costInputRef.showError(inputName)
      const errorName = this.getErrorName(errorInfo)
      const foundErrorInfo = _.find(this.stepCostErrors, { name: errorName })
      if (!foundErrorInfo) {
        this.stepCostErrors.push({ name: errorName, info: errorInfo })
      }
    },
    validateStepRewardType () {
      let errorMsgI18nKey = ''
      _.forEach(this.activeStepNames, stepName => {
        const stepConfig = this.hireStepConfig[stepName]
        _.forEach(HIRE_TYPE, hireType => {
          const hireConfig = stepConfig[hireType]
          const rewardType = hireConfig.type
          if (!rewardType) {
            errorMsgI18nKey = 'rewardTypeIsRequired'
            return false
          }
          if (!this.showPointsReferralConfig && rewardType === REWARD_TYPE.point) {
            hireConfig.type = null
            errorMsgI18nKey = 'rewardTypeIsRequired'
            return false
          }
        })
      })
      return errorMsgI18nKey
    },
    validate () {
      const referralInfo = this.referralInfo
      const hireNumber = referralInfo.hireNumber
      const everyHireCost = referralInfo.everyHireCost
      const totalActualCost = this.totalActualCost
      let errorMsgI18nKey = ''
      if (hireNumber < 1) {
        errorMsgI18nKey = 'hireNumberTooSmall'
      } else if (everyHireCost.cash < 100 && everyHireCost.point < 100) {
        errorMsgI18nKey = 'everyHireCostTooSmall'
      } else if (this.stepCostErrors.length > 0) {
        errorMsgI18nKey = 'stepCostHasError'
      } else if (totalActualCost.cash <= 0 && totalActualCost.point <= 0) {
        errorMsgI18nKey = 'actualCostTooSmall'
      } else {
        errorMsgI18nKey = this.validateStepRewardType()
      }
      if (errorMsgI18nKey) {
        this.$message.error(this.$t(`${this.i18nKeyPrefix}.message.${errorMsgI18nKey}`))
        return null
      }
      const isLiteReferralConfig = this.isLiteReferralConfig
      const referralData = {
        hire_count: referralInfo.hireNumber,
        budget: referralInfo.everyHireCost.cash,
        points_budget: referralInfo.everyHireCost.point,
        referral_amount: totalActualCost.cash,
        points_amount: totalActualCost.point,
        public: !referralInfo.onlyEmployee,
        steps_setting: null
      }
      if (isLiteReferralConfig) {
        referralData.public = true
      }
      const stepConfigData = {}
      _.forEach(HIRE_STEP, (stepName) => {
        const stepConfig = this.hireStepConfig[stepName]
        let stepDataName = stepName
        if (stepName === HIRE_STEP.apply) {
          stepDataName = 'applied'
        }
        if (stepName === HIRE_STEP.interview) {
          stepDataName = 'interviewed'
        }
        const stepData = stepConfigData[stepDataName] = {
          plan: {
            is_enable: stepConfig.plan.is_enable,
            cash: stepConfig.plan.cash,
            points: stepConfig.plan.point
          }
        }
        _.forEach(HIRE_TYPE, (hireType) => {
          const hireConfig = stepConfig[hireType]
          const hireData = stepData[hireType] = {
            type: isLiteReferralConfig ? REWARD_TYPE.cash : hireConfig.type
          }
          if (GROUP_NAMES[0].includes(stepName)) {
            _.forEach(REFERRER_TYPE, referrerType => {
              hireData[referrerType] = { ...hireConfig[referrerType] }
            })
          } else {
            hireData.count = hireConfig.direct.count
            hireData.amount = hireConfig.direct.amount
          }
          if (hireType === HIRE_TYPE.notEmployee && this.onlyEmployee) {
            if (isLiteReferralConfig) {
              const hireData = stepData[hireType]
              const employeeData = stepData[HIRE_TYPE.employee]
              if (GROUP_NAMES[0].includes(stepName)) {
                _.forEach(REFERRER_TYPE, (referrerType) => {
                  hireData[referrerType].count = employeeData[referrerType].count
                  hireData[referrerType].amount = employeeData[referrerType].amount
                })
              } else {
                hireData.count = employeeData.count
                hireData.amount = employeeData.amount
              }
            } else {
              const hireData = stepData[hireType]
              if (GROUP_NAMES[0].includes(stepName)) {
                _.forEach(REFERRER_TYPE, (referrerType) => {
                  hireData[referrerType].amount = 0
                })
              } else {
                hireData.amount = 0
              }
            }
          }
        })
      })
      referralData.steps_setting = JSON.stringify(stepConfigData)
      const referralId = this.referralInfo.orginReferralData.setting?.id
      if (referralId) {
        referralData.id = referralId
      }
      return referralData
    },
    handleUserModeBtnClick () {
      if (this.disableUserModeBtn) {
        return
      }
      this.isUserMode = !this.isUserMode
    },
    async handleSave () {
      if (this.saving) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        this.saving = true
        const referralData = this.validate()
        if (!referralData) {
          return
        }
        const resp = await this.submitJobReferral({ jobId: this.jobId, referralInfo: referralData })
        if (resp && resp.success) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$router.go(-1)
        } else {
          this.$message.error(this.$t('common.message.saveFail'))
        }
      } finally {
        this.saving = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleSaveDraft () {
      if (this.saving) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      try {
        this.saving = true
        const referralData = this.validate()
        if (!referralData) {
          return
        }
        const resp = await this.saveDraftJobReferral({ jobId: this.jobId, referralInfo: referralData })
        if (resp && resp.success) {
          this.$message.success(this.$t('common.message.saveSuccess'))
          this.$router.go(-1)
        } else {
          this.$message.error(this.$t('common.message.saveFail'))
        }
      } finally {
        this.saving = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleCancel () {
      this.$router.go(-1)
    },
    handleBackJobList () {
      this.$router.push('/v-admin/job-list/')
    }
  },
  watch: {
    'referralInfo.hireNumber' (newVal, oldVal) {
      if (this.loading || this.isOnlyEnableHireNumberInput) {
        return
      }
      this.resetStepPercent()
    },
    totalCost (newVal, oldVal) {
      if (this.loading || this.isOnlyEnableHireNumberInput) {
        return
      }
      _.forEach(oldVal, (val, costType) => {
        if (val !== newVal[costType]) {
          this.resetStepPercent(costType)
        }
      })
    },
    costTypeActiveStepNames () {
      if (this.loading) {
        return
      }
      this.resetUserMode()
      this.resetStepPercent()
    }
  }
}
</script>

<style lang="scss" scoped>
.job-referral-editor {
  justify-content: center;

  ::v-deep {
    .aside-container {
      padding: 0 5px;
      position: relative;
    }

    .main-container {
      width: 820px;
    }
  }

  .aside-head {
    height: 75px;
    border-bottom: 1px solid #b2b2b2;
    position: relative;

    .back-job-list-btn {
      font-size: 16px;
      position: absolute;
      bottom: 0;
    }
  }

  .aside-body {
    .referral-status-history {
      width: 100%;
      padding-left: 10px;
      position: absolute;
      bottom: 240px;
      left: 0;

      .history-item {
        margin-bottom: 10px;

        &:last-child {
          margin-bottom: 0;
        }

        .history-status {
          color: #A39E9E;
        }
      }
    }
  }

  .referral-info-head {
    height: 45px;
    border-bottom: 1px solid #b2b2b2;

    .referral-title {
      font-size: 20px;
    }
  }

  .referral-info-body {
    padding-left: 10px;
    position: relative;

    .referral-editor {
      margin-top: 20px;

      .editor-line {
        display: flex;
        margin-bottom: 20px;

        .line-label {
          width: 150px;
          line-height: 32px;
          margin-right: 15px;
          vertical-align: middle;
          word-break: break-word;
          white-space: pre-wrap;
        }

        .referral-tip-icon {
          font-size: 16px;
          color: #aeaeae;
          cursor: help;
        }

        .line-content {
          flex: 1;
          display: flex;
          height: 32px;
          vertical-align: middle;

          &.short-content {
            .el-input, .referral-input {
              width: 135px;
              margin-right: 10px;
            }

            .rect-switch {
              width: 80px;
              height: 24px;
              margin: auto 0;
              line-height: 24px;
            }
          }

          .el-input {
            ::v-deep {
              .el-input__inner {
                height: 32px;
                line-height: 32px;
              }
            }
          }

          .referral-input {
            ::v-deep {
              .referral-input-prefix {
                line-height: 32px;
              }

              .el-input {
                .el-input__inner {
                  height: 32px;
                  line-height: 32px;
                }
              }
            }
          }

          .referral-input {
            line-height: 32px;
          }
        }

        &.every-hire-cost {
          .line-label {
            label {
              vertical-align: middle;
            }

            .referral-tip-icon {
              vertical-align: middle;
            }
          }
        }

        &.hire-step-cost {
          .line-label {
            line-height: initial;
          }

          .line-content {
            display: block;
            height: auto;

            .cost-config {
              display: flex;
              justify-content: space-between;

              .cost-config-group {
                width: 49%;
                padding: 20px 10px;
                border: 1px solid #e6e6e6;
              }
            }

            .step-choose-container {
              display: flex;
              justify-content: space-between;

              .step-choose {
                .el-checkbox {
                  color: inherit;

                  ::v-deep {
                    .el-checkbox__input {
                      .el-checkbox__inner {
                        border-color: var(--color);
                      }

                      &.is-checked {
                        .el-checkbox__inner {
                          background-color: var(--color);
                          border-color: var(--color);
                        }

                        + .el-checkbox__label {
                          color: inherit;
                        }

                        &.is-disabled {
                          .el-checkbox__inner {
                            &::after {
                              border-color: #fff;
                            }
                          }
                        }
                      }

                      &.is-disabled {
                        + span.el-checkbox__label {
                          color: inherit;
                        }
                      }
                    }
                  }
                }

                .referral-tip-icon {
                  margin-left: 5px;
                  vertical-align: text-top;
                }
              }
            }

            .type-select-container {
              margin-top: 25px;

              .referral-type-select {
                display: block;
                margin-bottom: 10px;
              }
            }

            .cost-percent-slider {
              margin-top: 20px;
              position: relative;

              .slider-wrapper {
                padding-right: 30px;

                .mutil-range-slider {
                  margin-bottom: 25px;

                  &:last-child {
                    margin-bottom: 0;
                  }
                }
              }

              .user-mode-btn {
                width: 20px;
                height: 20px;
                border: 1px solid #000;
                border-radius: 50%;
                text-align: center;
                cursor: pointer;
                position: absolute;
                top: -3px;
                right: 0;

                i {
                  margin-top: 1px;
                  color: #000;
                  font-size: 16px;
                  transform: rotateX(180deg) rotateZ(90deg);
                }
              }
            }
          }
        }

        &.cost-detail {
          margin-top: 25px;
          padding-top: 25px;

          .cost-card-container {
            display: flex;
            border: 1px solid #e0e0e0;

            .detail-label {
              width: 120px;
              border-right: 0;
              padding-left: 10px;
            }

            .group-card-container {
              flex: 1;
              display: flex;
              min-width: 267px;
              justify-content: space-between;

              .step-group-card {
                flex: 1;
                border: 2px solid #eeeeee;
                border-top: 0;
                border-bottom: 0;

                & + .step-group-card {
                  margin-left: 10px;
                }

                &:last-child {
                  border-right: 0;
                }

                .group-card {
                  display: flex;

                  .step-card {
                    flex: 1;
                    display: flex;
                    padding: 0 5px;
                    justify-content: center;
                    position: relative;

                    .step-color-bar {
                      height: 5px;
                      width: 100%;
                      position: absolute;
                      top: 0;
                      left: 0;
                    }

                    .step-cost {
                      height: 32px;
                      width: 100%;
                      text-align: center;
                      transform: translateY(-100%);
                      position: absolute;
                      top: -3px;
                      left: 0;

                      .cost-percent {
                        height: 50%;

                        &.hidden {
                          visibility: hidden;
                        }
                      }

                      .cost-number {
                        span {
                          margin: 5px;
                        }
                      }
                    }

                    .referral-cost-input {
                      line-height: 32px;

                      &.limit-width {
                        &.cost-input-2 {
                          ::v-deep.number-input {
                            width: 40px;
                          }
                        }

                        ::v-deep.number-input {
                          width: 70px;
                        }
                      }

                      ::v-deep {
                        .el-input__prefix {
                          left: 1px;

                          .iconren {
                            font-size: 18px;
                            color: #b2b2b2;
                            line-height: inherit;
                          }
                        }
                      }
                    }

                    .cost-list {
                      flex: 1;
                    }
                  }
                }

                .step-group-cost {
                  height: 32px;
                  background: #eeeeee;
                  font-weight: bold;
                  text-align: right;
                  line-height: 32px;

                  span {
                    margin: 0 3px;
                  }

                  .van-icon {
                    margin-right: 10px;
                    font-size: 18px;
                    vertical-align: text-bottom;
                  }
                }
              }
            }

            .label-list {
              .detail-row {
                .self-detail-row,
                .child-detail-row {
                  label {
                    line-height: 32px;
                  }
                }

                .child-detail-row {
                  padding-left: 12px;
                }
              }
            }

            .cost-list {
              .detail-row {
                &.step-name {
                  text-align: center;
                  line-height: 32px;
                }
              }
            }

            .detail-row-list {
              .detail-row {
                min-height: 32px;
                margin: 5px 0;

                .self-detail-row,
                .child-detail-row {
                  min-height: 32px;
                  margin: 5px 0;
                }
              }
            }
          }
        }
      }

      .el-input {
        &:not(.referral-input) {
          ::v-deep {
            .el-input__inner {
              padding: 0 5px;
            }
          }
        }
      }
    }

    .referral-total-cost-container {
      margin: 25px 0 35px;
      font-size: 18px;
      font-weight: bold;
      text-align: right;

      .label {
        font-size: 14px;
        font-weight: normal;
      }

      .plan-cost {
        padding-right: 10px;
        position: absolute;
        top: 0;
        right: 0;
      }

    }

    .bottom-btns {
      display: flex;
      height: 30px;

      .cancel-btn {
        margin-left: 0;
        margin-right: auto;
      }

      .el-button {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }
}
.page-en {
  .job-referral-editor{
    .referral-info-body {
      .referral-editor {
        .editor-line {
          &.hire-step-cost {
            .line-content {
              .step-choose-container {
                .step-choose {
                  .el-checkbox {
                    ::v-deep {
                      .el-checkbox__label {
                        padding-left: 3px;
                      }
                    }
                  }
                }
              }
            }
          }

          &.cost-detail {
            .cost-card-container {
              .detail-label {
                width: 145px;
              }
            }
          }

          &.only-employee {
            .line-label {
              line-height: initial;
            }
          }
        }
      }
    }
  }
}
</style>
<style lang="scss">
.referral-tip-popper {
  max-width: 40%;

  &.el-tooltip__popper.is-dark {
    color: #b5b5b5;
  }
}
.el-popper.referral-chain-tooltip {
  background: #000;

  &[x-placement^=top]{
    .popper__arrow {
      border-top-color: #000;

      &::after {
        border-top-color: #000;
      }
    }
  }

  &[x-placement^=bottom]{
    .popper__arrow {
      border-bottom-color: #000;

      &::after {
        border-bottom-color: #000;
      }
    }
  }

}
</style>
